import { useState } from "react";
import { createPortal } from "react-dom";
import ConfirmModal from "./ConfirmModal";
import { formatImportLines } from "./ImportTasksModal";
import { downloadText, slug } from "../download";

/**
 * Review popup for tasks employees have proposed (employee → admin), shown in
 * the same visual language as InboundChangesModal (the Google Sheet review),
 * though the two share no code — nothing here has touched real tasks yet, so
 * there's no snapshot/diff, just a batch to approve or reject. Each batch is
 * one employee's submission and is approved/rejected independently.
 */
const fmt = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

function EstimateBadge({ item, currencySymbol, timeUnit }) {
  if (item.client_estimate_price !== null && item.client_estimate_price !== undefined) {
    return <span className="diff-val">{currencySymbol}{fmt(item.client_estimate_price)}</span>;
  }
  if (item.client_estimate_hours !== null && item.client_estimate_hours !== undefined) {
    return <span className="diff-val">{fmt(item.client_estimate_hours)} {timeUnit}</span>;
  }
  return <span className="diff-val empty">no estimate</span>;
}

export default function TaskProposalsModal({
  open,
  proposals = [],
  currencySymbol = "$",
  timeUnit = "hours",
  hourlyRate = null,
  sheetName = "",
  busy = false,
  onApprove,
  onReject,
  onApproveAll,
  onRejectAll,
  onClose,
}) {
  // "approve" | "reject" | null — the bulk action waiting on a confirmation.
  // Both are confirmed: one click resolving every request at once is worth a
  // second look, and rejecting is terminal.
  const [confirm, setConfirm] = useState(null);

  if (!open || proposals.length === 0) return null;

  const totalTasks = proposals.reduce((s, p) => s + (p.items?.length || 0), 0);

  // Rejecting is terminal, so a batch you don't want here is gone for good.
  // Downloading writes it back out in the import format (with the proposer as
  // the assignee), which is how a rejected batch gets moved to another page or
  // kept for later: paste the file into "Import from text…" there.
  const downloadOne = (p) =>
    downloadText(
      `${slug(sheetName) || "tasks"} - ${slug(p.employee_name) || "proposed"}.txt`,
      formatImportLines(p.items, { timeUnit, hourlyRate, assignee: p.employee_name })
    );

  const downloadAll = () =>
    downloadText(
      `${slug(sheetName) || "tasks"} - proposed tasks.txt`,
      proposals
        .map((p) =>
          formatImportLines(p.items, { timeUnit, hourlyRate, assignee: p.employee_name })
        )
        .filter(Boolean)
        .join("\n")
    );

  return createPortal(
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div
          className="modal-card inbound-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          style={{ maxWidth: 640 }}
        >
          <h3>Tasks proposed by employees</h3>
          <p className="muted" style={{ marginTop: 8 }}>
            Review each request below, then approve to add the tasks (assigned
            automatically to whoever proposed them) or reject to discard it.
            Rejecting is final, so download a request first if you want to keep it
            or import it onto another page later.
          </p>

          <div
            className="inbound-summary"
            style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}
          >
            <div className="row-h" style={{ gap: 8 }}>
              <span className="pill pill-add">{proposals.length} request{proposals.length === 1 ? "" : "s"}</span>
              <span className="pill pill-update">{totalTasks} task{totalTasks === 1 ? "" : "s"}</span>
            </div>
            {/* All-scope actions, only worth showing when there's more than one
                request — with a single one the buttons on the request itself
                already do the same thing. */}
            {proposals.length > 1 && (
              <div className="row-h" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="ghost"
                  onClick={downloadAll}
                  title="Save every request as one importable list"
                >
                  Download all
                </button>
                <button
                  type="button"
                  className="danger-action"
                  onClick={() => setConfirm("reject")}
                  disabled={busy}
                >
                  Reject all
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => setConfirm("approve")}
                  disabled={busy}
                >
                  Approve all {totalTasks}
                </button>
              </div>
            )}
          </div>

          <div className="inbound-body">
            {proposals.map((p) => (
              <section key={p.id} style={{ marginBottom: 18 }}>
                <h4 className="diff-section">
                  {p.employee_name} · {p.items.length} task{p.items.length === 1 ? "" : "s"}
                </h4>
                {p.items.map((item, i) => (
                  <div className="diff-row diff-add" key={i}>
                    <div className="diff-head">
                      <span className="diff-mark mark-add">+</span>
                      <span className="diff-name">
                        {item.url ? (
                          <a href={item.url} target="_blank" rel="noreferrer">{item.name}</a>
                        ) : (
                          item.name
                        )}
                      </span>
                    </div>
                    <div className="diff-fields">
                      <div className="diff-field">
                        <span className="diff-label">Estimate</span>
                        <EstimateBadge item={item} currencySymbol={currencySymbol} timeUnit={timeUnit} />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="row-h" style={{ justifyContent: "space-between", marginTop: 10, gap: 8 }}>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => downloadOne(p)}
                    title="Save these tasks as an importable list (name - estimate - link - assignee)"
                  >
                    Download list
                  </button>
                  <div className="row-h" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="danger-action"
                      onClick={() => onReject(p.id)}
                      disabled={busy}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="primary"
                      onClick={() => onApprove(p.id)}
                      disabled={busy}
                    >
                      Approve {p.items.length} task{p.items.length === 1 ? "" : "s"}
                    </button>
                  </div>
                </div>
              </section>
            ))}
          </div>

          <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} disabled={busy}>Close</button>
          </div>
        </div>
      </div>

      {/* Sibling of the backdrop, never a child of it: React events bubble
          through the React tree, not the DOM, so a click inside this popup's
          own portal would otherwise hit the backdrop's onClick and close the
          review underneath it. */}
      <ConfirmModal
        open={confirm !== null}
        title={
          confirm === "reject"
            ? `Reject all ${proposals.length} requests?`
            : `Approve all ${proposals.length} requests?`
        }
        description={
          confirm === "reject"
            ? `All ${totalTasks} proposed tasks are discarded and the requests can't be reopened. Download the list first if you want to keep them.`
            : `Adds all ${totalTasks} tasks to this page, each assigned to whoever proposed it.`
        }
        confirmLabel={confirm === "reject" ? "Reject all" : "Approve all"}
        danger={confirm === "reject"}
        onConfirm={() => {
          const action = confirm;
          setConfirm(null);
          if (action === "reject") onRejectAll();
          else onApproveAll();
        }}
        onCancel={() => setConfirm(null)}
      />
    </>,
    document.body
  );
}
